import { Alert, Badge, Button, Descriptions, List } from "antd";
import { useMemo } from "react";
import { FileOutlined, DownloadOutlined } from "@ant-design/icons";
import Loader from "../../../common/loader/Loader";
import {
  selectCurrentBusiness,
  selectCurrentBusinessFiles,
  selectIsLoadingBusinessFiles,
} from "../../businessesSlice";
import { useAppSelector } from "../../../../app/hooks";
import {
  canOpenBusinessFile,
  getFileDisplayName,
  openBusinessFile,
} from "./businessFiles.helpers";
import AdyenDocumentPanel from "../Adyen-Documents/AdyenDocumentPanel";
import AdyenDocumentStateAlert from "../Adyen-Documents/AdyenDocumentStateAlert";
import { buildAdyenFileAnnotations } from "../Adyen-Documents/adyenDocumentState.helpers";
import { useAdyenDocumentState } from "../Adyen-Documents/useAdyenDocumentState";
import { useBusinessFilesRefresh } from "./useBusinessFilesRefresh";

export default function BusinessFiles({ active }: { active: boolean }) {
  const business = useAppSelector(selectCurrentBusiness);
  const businessFiles = useAppSelector(selectCurrentBusinessFiles);
  const isLoadingBusinessFiles = useAppSelector(selectIsLoadingBusinessFiles);

  const {
    rows: adyenDocumentRows,
    error: adyenDocumentsError,
    refetch: refetchAdyenDocuments,
  } = useAdyenDocumentState(business?._id);

  useBusinessFilesRefresh(active);

  const adyenAnnotations = useMemo(
    () =>
      buildAdyenFileAnnotations({
        rows: adyenDocumentRows,
        files: businessFiles ?? [],
      }),
    [adyenDocumentRows, businessFiles]
  );

  return (
    <div style={{ position: "relative", minHeight: "100px" }}>
      {adyenDocumentsError && (
        <AdyenDocumentStateAlert
          error={adyenDocumentsError}
          onRetry={refetchAdyenDocuments}
          style={{ marginBottom: 8 }}
        />
      )}
      {!isLoadingBusinessFiles && businessFiles && businessFiles.length > 0 && (
        <>
          <Descriptions title="Additional Files"></Descriptions>

          <List
            size="small"
            dataSource={businessFiles}
            renderItem={(item, index) => {
              const annotation = adyenAnnotations[index];
              return (
                <List.Item
                  key={`${item.type}-${item.fileName}-${index}`}
                  style={{ display: "block" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <>
                          <FileOutlined />
                          <span
                            style={{
                              marginLeft: "4px",
                              width: "200px",
                              display: "inline-block",
                            }}
                          >
                            {getFileDisplayName(item.type)}
                          </span>

                          <br />
                          <span style={{ opacity: "0.7", fontSize: "smaller" }}>
                            {" "}
                            {item.fileExtension}
                          </span>
                        </>
                      }
                      title={
                        <span>
                          {item?.fileName || <Badge count={"Missing"} />}
                        </span>
                      }
                    ></List.Item.Meta>

                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={() => openBusinessFile(item)}
                      disabled={!item.fileName || !canOpenBusinessFile(item)}
                    ></Button>
                  </div>

                  {annotation?.row && business?._id && (
                    <AdyenDocumentPanel
                      businessId={business._id}
                      row={annotation.row}
                      status={annotation.status}
                      showControl={annotation.showControl}
                      onSubmitted={refetchAdyenDocuments}
                    />
                  )}

                  {annotation?.status === "unattributable" && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginTop: 8 }}
                      message="Cannot be matched to a file"
                      description="Adyen holds state for this document type, but the read reports no storage location, so which of these files a submission would send cannot be told apart."
                    />
                  )}
                </List.Item>
              );
            }}
          />
        </>
      )}
      <div>
        {isLoadingBusinessFiles && <Loader text="Fetching Business Files" />}
      </div>
    </div>
  );
}
